import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../redux/store';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { ref as dbRef, onValue, off } from 'firebase/database';
import { db, rtdb } from '../../../firebase';
import { useCurrency } from '@cosmoola/react-cosmoola-js';
import axios from 'axios';
import appLogo from '../../../assets/app/logo.png';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Alert,
  useMediaQuery,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import {
  Car, MapPin, ChevronDown, Star, Navigation2, Clock, Tag, GitMerge,
  Image as ImageIcon, Flag, AlertCircle, RotateCcw, Home as HomeIcon,
  PhoneCall, CloudOff, Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { calculateRideEarnings, RideRateType } from '../../../redux/riderServiceFeeSlice';

import countries, { CountryInfo } from '../../../GLOBAL/Countries';
import {
  CAR_TYPES, BIKE_TYPES, JET_TYPES, RIDE_TYPES, CAR_BRANDS,
  CLASS_TYPE, carSizeName, carBrandName, classTypeName,
  fuelTypeName, transmissionName,
} from '../../../CATEGORIES/OperatorCategories';

declare global {
  interface Window {
    google: any;
  }
}

/* ═══════════════════════════════════════════════════════════
   Shared helpers — kept identical to BookRide.tsx so the fee
   preview here and the fee actually charged server-side never
   drift apart.
═══════════════════════════════════════════════════════════ */
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return +(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2);
}

function stripDialCode(raw: string, dialCode: string): string {
  const t = raw.trim();
  if (t.startsWith(dialCode)) return t.slice(dialCode.length).trim();
  for (const c of countries) {
    if (t.startsWith(c.phoneCode)) return t.slice(c.phoneCode.length).trim();
  }
  return t.replace(/^\+/, '');
}

const sumSelectedExtras = (
  allExtras: Record<string, number> | undefined,
  selected: string[]
): number => {
  if (!allExtras || !selected?.length) return 0;
  return selected.reduce((sum, key) => sum + (Number(allExtras[key]) || 0), 0);
};

const makeIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `rk_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

type RRT = RideRateType;
const DEFAULT_RATE_TYPE: RRT = 'perKm';
const resolveRateType = (rt?: string | null): RRT => ((rt as RRT) || DEFAULT_RATE_TYPE);

/* ── Rate-type routing — decides which extra input the booking modal
      needs, and what data shape gets sent to checkout. ── */
type RateCategory = 'people' | 'duration' | 'hourly' | 'metered';
const PEOPLE_RATE_TYPES = new Set<RRT>(['perPerson', 'perPersonPerKm', 'perSeat']);
const DURATION_RATE_TYPES = new Set<RRT>(['perDay', 'perWeek', 'perMonth', 'perYear']);
const rateCategoryOf = (rt?: string | null): RateCategory => {
  const r = resolveRateType(rt);
  if (PEOPLE_RATE_TYPES.has(r)) return 'people';
  if (DURATION_RATE_TYPES.has(r)) return 'duration';
  if (r === 'perHour') return 'hourly';
  return 'metered'; // perKm, perCar, perSurge — driven by trip distance/time
};
const DURATION_UNIT_LABEL: Partial<Record<RRT, string>> = {
  perDay: 'day', perWeek: 'week', perMonth: 'month', perYear: 'year',
};
const HOURLY_MAX_FALLBACK = 12;
const hourlyMaxFor = (listing: Listing): number => {
  const raw = listing.maxHours;
  return Number(raw) > 0 ? Number(raw) : HOURLY_MAX_FALLBACK;
};
const durationMaxFor = (listing: Listing, rt: RRT): number => {
  const raw =
    rt === 'perDay' ? listing.maxDays :
    rt === 'perWeek' ? listing.maxWeeks :
    rt === 'perMonth' ? listing.maxMonths :
    rt === 'perYear' ? listing.maxYears :
    undefined;
  return Number(raw) > 0 ? Number(raw) : 365;
};

function addUnits(date: Date, count: number, unit: string): Date {
  const d = new Date(date);
  if (unit === 'day') d.setDate(d.getDate() + count);
  else if (unit === 'week') d.setDate(d.getDate() + count * 7);
  else if (unit === 'month') d.setMonth(d.getMonth() + count);
  else if (unit === 'year') d.setFullYear(d.getFullYear() + count);
  return d;
}

const LUXURY_CLASS_IDS = new Set<number>([CLASS_TYPE.LUXURY, CLASS_TYPE.ULTRA_LUXURY]);
const isLuxuryClass = (classType?: number) => classType !== undefined && LUXURY_CLASS_IDS.has(classType);

const discountPct = (d: any): number =>
  !d ? 0 : d.is80 ? 80 : d.is50 ? 50 : d.is25 ? 25 : d.is10 ? 10 : d.is5 ? 5 : 0;

const ALL_VEHICLE_TYPES = [...(CAR_TYPES as any[]), ...(BIKE_TYPES as any[]), ...(JET_TYPES as any[])];
function vehicleTypeItem(id: any): any {
  return ALL_VEHICLE_TYPES.find((v: any) => v.id === id);
}
function vehicleName(id: any): string {
  return (vehicleTypeItem(id) as any)?.name ?? 'Vehicle';
}
function discKeyFromObj(obj: Record<string, boolean> = {}) {
  if (obj.is80) return 'is80'; if (obj.is50) return 'is50';
  if (obj.is25) return 'is25'; if (obj.is10) return 'is10';
  if (obj.is5) return 'is5'; return 'none';
}
const DISC_LABELS: Record<string, string> = {
  none: '', is5: '5%', is10: '10%', is25: '25%', is50: '50%', is80: '80%',
};
function extrasToArr(obj: Record<string, number> = {}): { name: string; price: number }[] {
  return Object.entries(obj).map(([name, price]) => ({ name, price }));
}

interface Listing {
  id: string;
  listingId?: string;
  hostId?: string;
  driverId?: string;
  isSelected?: boolean;
  brand?: number;
  model?: string;
  year?: number | string;
  licensePlate?: string;
  rate?: number;
  rateType?: string;
  vehicleType?: number;
  rideType?: number;
  classType?: number;
  carSize?: number;
  fuelType?: number;
  transmission?: number;
  capacity?: number | string;
  seats?: number;
  photos?: string[];
  rating?: number;
  rideSharing?: boolean;
  allowParcel?: boolean;
  extras?: Record<string, number>;
  isPublished?: boolean;
  currency?: string;
  city?: string;
  country?: string;
  serviceRadiusKm?: number | string;
  description?: string;
  discountApplied?: { is5?: boolean; is10?: boolean; is25?: boolean; is50?: boolean; is80?: boolean };
  baseFare?: number;
  perKmRate?: number;
  perMinuteRate?: number;
  surgeMultiplier?: number;
  guestCount?: number;
  maxDays?: number;
  maxWeeks?: number;
  maxMonths?: number;
  maxYears?: number;
  maxHours?: number;
  [key: string]: any;
}

interface LivePos {
  lat: number;
  lng: number;
  isAvailable?: boolean;
  heading?: number;
  ts?: number;
}

interface Trip {
  id: string;
  pickup: string;
  dropoff: string;
  pickupLat?: number;
  pickupLng?: number;
  dropoffLat?: number;
  dropoffLng?: number;
}

type TripType = 'oneway' | 'roundtrip' | 'multitrip';

const RATE_LABELS: Record<string, string> = {
  perKm: '/km',
  perPerson: '/person',
  perPersonPerKm: '/person/km',
  perDay: '/day',
  perCar: '/trip',
  perSeat: '/seat',
  perMinute: '/min',
  perHour: '/hour',
  perWeek: '/week',
  perMonth: '/month',
  perYear: '/year',
  perSurge: '/trip',
};

const FN_BASE = 'https://europe-west1-eartlas.cloudfunctions.net';
const REQUEST_RIDE_URL = `${FN_BASE}/requestRide`;
const RIDE_CHECKOUT_URL = `${FN_BASE}/createRideCheckoutSession`;
const CANCEL_RIDE_URL = `${FN_BASE}/cancelRide`;
const PENDING_RIDE_KEY = 'heartlas_pending_ride';

const TRIP_STATUS_CFG: Record<string, { label: string; color: string; sub: string }> = {
  requested: { label: 'Finding Your Driver…', color: '#8A7660', sub: "Hang tight, we're matching you" },
  accepted: { label: 'Driver Accepted!', color: '#1A9E6E', sub: 'Driver is on the way' },
  arriving: { label: 'Driver Arriving', color: '#4A9EFF', sub: 'Almost there…' },
  ongoing: { label: 'Trip in Progress', color: '#1A9E6E', sub: "You're on your way" },
  completed: { label: 'Trip Complete 🎉', color: '#1A9E6E', sub: 'Thanks for riding with Heartlas' },
  cancelled: { label: 'Ride Cancelled', color: '#C8202E', sub: 'Your ride was cancelled' },
  no_driver: { label: 'No Driver Available', color: '#C8202E', sub: 'No drivers found nearby' },
  finding_driver: { label: 'Finding Driver…', color: '#8A7660', sub: 'Searching nearby drivers' },
};

/* ── Heartlas colors — extended with the extra tokens the dc- card system
      (copied from DriverCars.tsx) needs, so both files share one palette. ── */
const H = {
  bg: '#FBF6EE',
  panel: '#FFFFFF',
  panelSoft: '#FCEFD4',
  dark: '#1B0E06',
  textMuted: '#7A6653',
  textFaint: '#A6947F',
  red: '#C8202E',
  redDark: '#9E1521',
  redSoft: '#FBE7E9',
  gold: '#F2A93E',
  goldDark: '#D4900F',
  goldSoft: '#FCEFD4',
  green: '#1DB954',
  greenSoft: '#E5F8ED',
  amber: '#D4900F',
  amberSoft: '#FCEFD4',
  blue: '#3D7BD9',
  blueSoft: '#EAF1FC',
  border: '#EEDFC4',
  divider: '#F3E6CE',
};
// Kept as an alias so the rest of the form/booking UI (written against the
// old name) doesn't need touching.
const HEARTLAS_COLORS = H;

/* ═══════════════════════════════════════════════════════════
   dc- CARD SYSTEM — copied verbatim from DriverCars.tsx (same
   style id, so if both components are ever mounted at once they
   share one injected <style> tag instead of duplicating it).
   Only the bits the listing cards / spec panel need are pulled
   in here; anything modal/wizard-only from DriverCars is omitted
   since RiderHome doesn't render that UI.
═══════════════════════════════════════════════════════════ */
const DC_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;700;800&family=Montserrat:wght@400;500;600;700;800;900&display=swap');
.dc-root, .dc-root *, .dc-root *::before, .dc-root *::after{box-sizing:border-box;}
.dc-root{
  --serif:'Baloo 2',Georgia,serif;--sans:'Montserrat',-apple-system,sans-serif;
  width:100%;font-family:var(--sans);color:${H.dark};
}
.dc-root button{font-family:inherit;}

.dc-rev{opacity:0;transform:translateY(10px);transition:opacity .4s ease var(--d,0ms),transform .4s cubic-bezier(.22,1,.36,1) var(--d,0ms);}
.dc-on .dc-rev{opacity:1;transform:none;}

.dc-list{display:grid;grid-template-columns:1fr;gap:20px;}
@media(min-width:640px){.dc-list{grid-template-columns:repeat(2,1fr);}}
@media(min-width:1040px){.dc-list{grid-template-columns:repeat(3,1fr);}}

.dc-row-card{background:${H.panel};border:2px solid transparent;border-radius:20px;box-shadow:0 4px 16px rgba(27,14,6,.06);transition:box-shadow .25s ease,transform .25s cubic-bezier(.22,1,.36,1),border-color .2s;overflow:hidden;display:flex;flex-direction:column;}
.dc-row-card:hover{box-shadow:0 14px 30px rgba(27,14,6,.12);transform:translateY(-4px);}

.dc-row-head{display:flex;align-items:center;gap:14px;padding:14px;cursor:pointer;}

.dc-row-thumb{width:84px;height:84px;border-radius:16px;overflow:hidden;flex-shrink:0;position:relative;background:linear-gradient(135deg,${H.panelSoft},${H.bg});}
.dc-row-thumb img{width:100%;height:100%;object-fit:cover;display:block;}
.dc-row-thumb-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:${H.goldDark};background:linear-gradient(135deg,${H.panelSoft},${H.bg});}
.dc-row-thumb-ph svg{width:30px;height:30px;opacity:.5;}

.dc-row-meta{flex:1;min-width:0;}
.dc-row-title{font-family:var(--serif);font-size:17px;font-weight:700;color:${H.dark};margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.dc-row-sub{font-size:12px;color:${H.textFaint};margin-bottom:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

.dc-status-pill{display:inline-flex;align-items:center;gap:7px;background:${H.bg};padding:5px 12px 5px 10px;border-radius:999px;font-size:12px;font-weight:600;color:${H.textMuted};}
.dc-status-pill-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}

.dc-row-side{display:flex;flex-direction:column;align-items:flex-end;gap:6px;flex-shrink:0;}
.dc-row-rate{font-family:var(--serif);font-size:16px;font-weight:700;color:${H.redDark};}
.dc-row-rate-unit{font-size:9px;color:${H.goldDark};font-weight:700;text-align:right;}
.dc-row-chevron{color:${H.textFaint};display:flex;transition:transform .3s cubic-bezier(.22,1,.36,1);}
.dc-row-chevron.open{transform:rotate(180deg);}

.dc-row-expand-wrap{display:grid;grid-template-rows:0fr;transition:grid-template-rows .4s cubic-bezier(.22,1,.36,1);}
.dc-row-expand-wrap.open{grid-template-rows:1fr;}
.dc-row-expand-inner{overflow:hidden;min-height:0;}
.dc-row-expand-page{transform-origin:top center;transform:scaleY(.97) rotateX(-6deg);opacity:0;transition:transform .38s cubic-bezier(.22,1,.36,1),opacity .3s ease;padding:0 16px 16px;border-top:1px solid ${H.border};margin-top:2px;}
.dc-row-expand-wrap.open .dc-row-expand-page{transform:scaleY(1) rotateX(0deg);opacity:1;}

.dc-spec-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin:14px 0 4px;}
.dc-spec-item{display:flex;flex-direction:column;gap:2px;background:${H.bg};border:1px solid ${H.border};border-radius:12px;padding:8px 11px;}
.dc-spec-lbl{font-size:8.5px;font-weight:800;letter-spacing:1px;color:${H.textFaint};text-transform:uppercase;}
.dc-spec-val{font-size:13px;font-weight:700;color:${H.dark};font-family:var(--sans);}

.dc-meta-row{display:flex;flex-wrap:wrap;gap:5px;margin:4px 0 10px;}
.dc-pill{display:flex;align-items:center;gap:4px;padding:3px 9px;border:1px solid;font-size:9px;font-weight:700;border-radius:20px;}
.dc-extras-row{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:10px;}
.dc-extra-tag{background:${H.bg};border:1px solid ${H.border};padding:2px 8px;font-size:9px;font-weight:600;color:${H.textMuted};border-radius:8px;}
.dc-loc-row{display:flex;align-items:center;gap:5px;margin-bottom:12px;font-size:10px;color:${H.textMuted};}
.dc-desc-row{font-size:12px;color:${H.textMuted};line-height:1.6;margin-bottom:12px;}

.dc-card-actions{display:flex;gap:8px;}
.dc-edit-btn{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;background:${H.goldSoft};border:1px solid ${H.gold};padding:9px 0;cursor:pointer;font-size:10px;font-weight:800;letter-spacing:.6px;color:${H.goldDark};font-family:var(--sans);border-radius:12px;transition:background .2s,transform .15s;}
.dc-edit-btn:hover{background:${H.gold};color:#fff;}
.dc-edit-btn:active{transform:translateY(1px);}
.dc-edit-btn:disabled{opacity:.55;cursor:not-allowed;}

.dc-stats{display:flex;gap:0;border:1px solid ${H.border};border-radius:16px;overflow:hidden;margin-bottom:20px;background:${H.panel};}
.dc-stat-item{flex:1;display:flex;flex-direction:column;align-items:center;gap:2px;padding:12px 6px;border-right:1px solid ${H.border};}
.dc-stat-item:last-child{border-right:none;}
.dc-stat-val{font-family:var(--serif);font-size:24px;font-weight:700;}
.dc-stat-lbl{font-size:8px;font-weight:800;letter-spacing:1.2px;color:${H.textFaint};text-transform:uppercase;}

.dc-empty{display:flex;flex-direction:column;align-items:center;gap:12px;padding:60px 24px;text-align:center;background:${H.panel};border:1px solid ${H.border};border-radius:18px;}
.dc-empty-icon{color:${H.goldDark};margin-bottom:4px;}
.dc-empty-title{font-family:var(--serif);font-size:23px;font-weight:700;color:${H.dark};}
.dc-empty-sub{font-size:12px;color:${H.textMuted};line-height:1.6;max-width:280px;}
.dc-loader{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:60px 0;}
.dc-loader-txt{color:${H.textMuted};font-size:12px;letter-spacing:.4px;}
.dc-spin{animation:dcSpin .8s linear infinite;}
@keyframes dcSpin{to{transform:rotate(360deg);}}
`;

/* ─── SpecItem / RowThumb — copied from DriverCars.tsx unchanged ─────────── */
function SpecItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="dc-spec-item">
      <span className="dc-spec-lbl">{label}</span>
      <span className="dc-spec-val">{value}</span>
    </div>
  );
}

/* ─── Duration calendar — cute inline month-grid picker for perDay/Week/
      Month/Year listings. Single-month view with nav arrows, gold/red
      Heartlas palette, gold range wash between start and computed end
      date. Renders identically in the desktop modal and the mobile
      bottom sheet since it's just a Box, no portal of its own. ─────── */
function DurationCalendarPicker({
  startDate, unitCount, unitLabel, onSelectStart,
}: {
  startDate: Date | null;
  unitCount: number;
  unitLabel: string;
  onSelectStart: (d: Date) => void;
}) {
  const [monthOffset, setMonthOffset] = useState(0);

  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const endDate = useMemo(
    () => (startDate ? addUnits(startDate, unitCount, unitLabel) : null),
    [startDate, unitCount, unitLabel]
  );

  const base = new Date();
  base.setMonth(base.getMonth() + monthOffset);
  const year = base.getFullYear();
  const month = base.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const grid: (Date | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const inRange = (d: Date) => !!(startDate && endDate && d > startDate && d < endDate);

  return (
    <Box sx={{ border: `1px solid ${HEARTLAS_COLORS.border}`, borderRadius: 2, p: 2, background: '#fff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <IconButton
          size="small"
          onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
          disabled={monthOffset === 0}
          sx={{ border: `1px solid ${HEARTLAS_COLORS.border}`, width: 30, height: 30 }}
        >
          <ChevronLeft size={15} />
        </IconButton>
        <Box sx={{ fontSize: 13, fontWeight: 700, color: HEARTLAS_COLORS.dark }}>
          {base.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
        </Box>
        <IconButton
          size="small"
          onClick={() => setMonthOffset((o) => o + 1)}
          sx={{ border: `1px solid ${HEARTLAS_COLORS.border}`, width: 30, height: 30 }}
        >
          <ChevronRight size={15} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', textAlign: 'center', mb: 0.5 }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <Box key={`${d}-${i}`} sx={{ fontSize: 10, fontWeight: 700, color: HEARTLAS_COLORS.textFaint, py: 0.5 }}>
            {d}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
        {grid.map((day, i) => {
          if (!day) return <Box key={i} />;
          const past = day < today;
          const isStart = startDate ? sameDay(day, startDate) : false;
          const isEnd = endDate ? sameDay(day, endDate) : false;
          const ranged = inRange(day);
          return (
            <Box key={i} sx={{ position: 'relative', display: 'flex', justifyContent: 'center', py: '2px' }}>
              {ranged && (
                <Box
                  sx={{
                    position: 'absolute', top: '50%', left: -4, right: -4, height: 30,
                    transform: 'translateY(-50%)', background: `${HEARTLAS_COLORS.gold}26`,
                  }}
                />
              )}
              <button
                type="button"
                disabled={past}
                onClick={() => onSelectStart(day)}
                style={{
                  position: 'relative', zIndex: 1, width: 30, height: 30, borderRadius: '50%',
                  border: isEnd && !isStart ? `1.5px solid ${HEARTLAS_COLORS.gold}` : 'none',
                  fontWeight: 700, fontSize: 12, cursor: past ? 'not-allowed' : 'pointer',
                  background: isStart
                    ? `linear-gradient(135deg, ${HEARTLAS_COLORS.red}, ${HEARTLAS_COLORS.gold})`
                    : 'transparent',
                  color: past ? HEARTLAS_COLORS.textFaint : isStart ? '#fff' : HEARTLAS_COLORS.dark,
                }}
              >
                {day.getDate()}
              </button>
            </Box>
          );
        })}
      </Box>

      {startDate && endDate && (
        <Box
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            mt: 1.5, pt: 1.5, borderTop: `1px solid ${HEARTLAS_COLORS.divider}`,
          }}
        >
          <Box>
            <Box sx={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textFaint }}>STARTS</Box>
            <Box sx={{ fontSize: 12, fontWeight: 700, color: HEARTLAS_COLORS.dark }}>
              {startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </Box>
          </Box>
          <Box sx={{ color: HEARTLAS_COLORS.gold, fontWeight: 700 }}>→</Box>
          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textFaint }}>ENDS</Box>
            <Box sx={{ fontSize: 12, fontWeight: 700, color: HEARTLAS_COLORS.dark }}>
              {endDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function RowThumb({ listing }: { listing: any }) {
  const typeItem = vehicleTypeItem(listing.vehicleType);
  const rideItem = RIDE_TYPES.find((r: any) => r.id === listing.rideType);
  const photo = listing.photos?.[0];
  const typeImg = typeItem?.image;
  const rideImg = rideItem?.image;

  const [photoFailed, setPhotoFailed] = useState(false);
  const [typeImgFailed, setTypeImgFailed] = useState(false);
  const [rideImgFailed, setRideImgFailed] = useState(false);

  if (photo && !photoFailed) {
    return <img src={photo} alt="" onError={() => setPhotoFailed(true)} />;
  }
  if (typeImg && !typeImgFailed) {
    return <img src={typeImg} alt="" onError={() => setTypeImgFailed(true)} />;
  }
  if (rideImg && !rideImgFailed) {
    return <img src={rideImg} alt="" onError={() => setRideImgFailed(true)} />;
  }
  return (
    <div className="dc-row-thumb-ph">
      {typeItem?.icon ?? <Car size={30} />}
    </div>
  );
}

type ContactState = {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  dialCode: string;
  phone: string;
};
const EMPTY_CONTACT: ContactState = {
  firstName: '', lastName: '', email: '', country: '', dialCode: '+254', phone: '',
};

const RiderHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, authLoaded } = useSelector((s: RootState) => s.auth);
  const userId = (user as any)?.userId || (user as any)?.uid;

  const fees = useSelector((s: RootState) => (s as any).riderServiceFees);
  const { convertRaw, symbol } = useCurrency();

  const money = useCallback(
    (amountEur: number | undefined | null) => {
      const n = Number(amountEur ?? 0);
      return `${symbol}${convertRaw(n, 'EUR').toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },
    [convertRaw, symbol]
  );

  const [mounted, setMounted] = useState(false);
  const [userLoc, setUserLoc] = useState<{ lat: number; lng: number } | null>(null);
  const [tripType, setTripType] = useState<TripType>('oneway');
  const [trips, setTrips] = useState<Trip[]>([{ id: '1', pickup: '', dropoff: '' }]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [livePos, setLivePos] = useState<Record<string, LivePos>>({});
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const pickupRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const dropoffRefs = useRef<Record<string, HTMLInputElement | null>>({});

  /* ── Booking step ── */
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [bookForSomeoneElse, setBookForSomeoneElse] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookingErr, setBookingErr] = useState('');

  // Rate-type-driven booking inputs — only one of these is ever relevant
  // per listing, decided by rateCategory below.
  const [guestCount, setGuestCount] = useState(1);
  const [durationCount, setDurationCount] = useState(1);
  const [hourCount, setHourCount] = useState(1);
  const [bookingStartDate, setBookingStartDate] = useState<Date | null>(null);

  const isMobileScreen = useMediaQuery('(max-width:640px)');

  const [addr, setAddr] = useState<ContactState>(EMPTY_CONTACT);
  const patchAddr = useCallback(
    (patch: Partial<ContactState>) => setAddr((p) => ({ ...p, ...patch })),
    []
  );
  const [recipient, setRecipient] = useState<ContactState>(EMPTY_CONTACT);
  const patchRecipient = useCallback(
    (patch: Partial<ContactState>) => setRecipient((p) => ({ ...p, ...patch })),
    []
  );

  /* ── Trip tracking (post-checkout) — replaces the old navigate('/bookride') ── */
  const [activeTrip, setActiveTrip] = useState<any>(null);
  const [bookedListing, setBookedListing] = useState<any>(null);
  const [resumeNotice, setResumeNotice] = useState<{ listingId?: string; driverName: string } | null>(null);
  const [rtdbOffline, setRtdbOffline] = useState(false);

  const submittingRef = useRef(false);
  const idempotencyKeyRef = useRef<string>('');

  /* CSS inject */
  useEffect(() => {
    const id = 'dc-css-heartlas';
    if (!document.getElementById(id)) {
      const tag = document.createElement('style');
      tag.id = id;
      tag.textContent = DC_CSS;
      document.head.appendChild(tag);
    }
    requestAnimationFrame(() => setMounted(true));
  }, []);

  /* ── Prefill "myself" contact ── */
  useEffect(() => {
    if (!user) return;
    const rawPhone = (user as any).contactNumber || (user as any).whatsappNumber || '';
    const dialMatch = rawPhone.match(/^(\+\d{1,4})\s?/);
    const dialCode = dialMatch ? dialMatch[1] : '+254';
    const phone = dialMatch ? rawPhone.replace(dialMatch[0], '') : rawPhone;
    setAddr({
      firstName: (user as any).firstName || '',
      lastName: (user as any).lastName || (user as any).surName || '',
      email: (user as any).contactEmail || (user as any).email || (user as any).emailAddress || '',
      country: (user as any).shippingAddress?.country || '',
      dialCode,
      phone,
    });
  }, [user]);

  useEffect(() => {
    if (authLoaded && !user) navigate('/AuthSwitch');
  }, [authLoaded, user, navigate]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setUserLoc({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (e) => console.warn('Geo:', e.message)
    );
  }, []);

  /* ── RTDB live positions — keyed by listingId, matches BookRide.tsx ── */
  useEffect(() => {
    const r = dbRef(rtdb, 'onlineDrivers');
    const cb = onValue(r, (snap) => setLivePos(snap.val() || {}));
    return () => off(r, 'value', cb as any);
  }, []);

  /* ── RTDB connectivity banner ── */
  useEffect(() => {
    const r = dbRef(rtdb, '.info/connected');
    const cb = onValue(r, (snap) => setRtdbOffline(snap.val() !== true));
    return () => off(r, 'value', cb as any);
  }, []);

  /* ── Return-from-Stripe: pick up ?tripId= / ?checkout=cancelled, strip
        them from the URL, and either resume trip tracking or offer to
        reopen the booking modal for the driver that was selected. ── */
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const tripIdParam = qs.get('trip_id');
    const canceledParam = qs.get('canceled');

    if (tripIdParam) setActiveTrip({ tripId: tripIdParam, status: 'requested' });

    if (canceledParam === 'true') {
      try {
        const raw = sessionStorage.getItem(PENDING_RIDE_KEY);
        if (raw) {
          const pending = JSON.parse(raw);
          setResumeNotice({ listingId: pending?.listingId, driverName: pending?.driverName || 'your driver' });
        }
      } catch { /* ignore malformed sessionStorage */ }
    }

    if (tripIdParam || canceledParam) {
      qs.delete('trip_id'); qs.delete('canceled'); qs.delete('session_id');
      const clean = qs.toString();
      navigate({ pathname: location.pathname, search: clean ? `?${clean}` : '' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Trip status listener — same shape BookRide.tsx listens to ── */
  useEffect(() => {
    if (!activeTrip?.tripId || !userId) return;
    const r = dbRef(rtdb, `riderTrips/${userId}/${activeTrip.tripId}`);
    const cb = onValue(r, (snap) => {
      const v = snap.val();
      if (!v) return;
      setActiveTrip((prev: any) => ({ ...prev, ...v }));
      setBookedListing((prev: any) => prev || v.driverSnapshot || null);
    });
    return () => off(r, 'value', cb as any);
  }, [activeTrip?.tripId, userId]);

  /* Google Places Autocomplete — pickups */
  useEffect(() => {
    if (!window.google?.maps?.places) return;
    trips.forEach((trip) => {
      if (!pickupRefs.current[trip.id]) return;
      const autocomplete = new window.google.maps.places.Autocomplete(
        pickupRefs.current[trip.id]!,
        { types: ['address'] }
      );
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();
        setTrips((prev) =>
          prev.map((t) =>
            t.id === trip.id
              ? { ...t, pickup: place.formatted_address || '', pickupLat: lat, pickupLng: lng }
              : t
          )
        );
      });
    });
  }, [trips]);

  /* Google Places Autocomplete — dropoffs */
  useEffect(() => {
    if (!window.google?.maps?.places) return;
    trips.forEach((trip) => {
      if (!dropoffRefs.current[trip.id]) return;
      const autocomplete = new window.google.maps.places.Autocomplete(
        dropoffRefs.current[trip.id]!,
        { types: ['address'] }
      );
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();
        setTrips((prev) =>
          prev.map((t) =>
            t.id === trip.id
              ? { ...t, dropoff: place.formatted_address || '', dropoffLat: lat, dropoffLng: lng }
              : t
          )
        );
      });
    });
  }, [trips]);

  /* ── Fleet listener — same collection/filter as BookRide.tsx:
        isPublished, then client-side isSelected && driverId ("who's
        actually driving this vehicle right now", Uber-style). ── */
  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'Fleet'), where('isPublished', '==', true));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setListings(snap.docs.map((d) => ({ id: d.id, listingId: d.id, ...d.data() } as Listing)));
        setLoading(false);
      },
      (err) => {
        console.error('Fleet listener failed:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleTripTypeChange = (newType: TripType) => {
    setTripType(newType);
    if (newType === 'oneway') {
      setTrips([{ id: '1', pickup: '', dropoff: '' }]);
    } else if (newType === 'roundtrip') {
      setTrips([
        { id: '1', pickup: trips[0]?.pickup || '', dropoff: trips[0]?.dropoff || '' },
        { id: '2', pickup: trips[0]?.dropoff || '', dropoff: trips[0]?.pickup || '' },
      ]);
    } else if (newType === 'multitrip') {
      if (trips.length === 1) {
        setTrips([
          { id: '1', pickup: trips[0]?.pickup || '', dropoff: trips[0]?.dropoff || '' },
          { id: '2', pickup: '', dropoff: '' },
        ]);
      }
    }
  };

  const addTrip = () => {
    const newId = (Math.max(...trips.map((t) => parseInt(t.id))) + 1).toString();
    setTrips([...trips, { id: newId, pickup: '', dropoff: '' }]);
  };

  const removeTrip = (id: string) => {
    if (trips.length > 1) setTrips(trips.filter((t) => t.id !== id));
  };

  const swapLocations = (id: string) => {
    setTrips((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              pickup: t.dropoff,
              dropoff: t.pickup,
              pickupLat: t.dropoffLat,
              pickupLng: t.dropoffLng,
              dropoffLat: t.pickupLat,
              dropoffLng: t.pickupLng,
            }
          : t
      )
    );
  };

  /* ── Merge Fleet listings + live RTDB positions, keyed by listingId ── */
  const liveMerged = useMemo(() => {
    const result: (Listing & { distKm?: number; eta?: number; live?: LivePos })[] = [];
    for (const listing of listings) {
      if (!listing.isSelected || !listing.driverId) continue;
      const lid = listing.listingId || listing.id;
      const live = livePos[lid];
      if (!live || typeof live.lat !== 'number' || typeof live.lng !== 'number') continue;
      const distKm = userLoc ? haversineKm(userLoc.lat, userLoc.lng, live.lat, live.lng) : undefined;
      const eta = distKm !== undefined ? Math.max(1, Math.round((distKm / 40) * 60)) : undefined;
      result.push({ ...listing, distKm, eta, live });
    }
    return result;
  }, [listings, livePos, userLoc]);

  const filtered = useMemo(() => {
    return liveMerged.filter((l) => {
      if (activeFilter === 'nearby') return (l.distKm ?? 9999) <= 10;
      if (activeFilter === 'cheap') return (l.rate || 0) <= 5;
      if (activeFilter === 'share') return l.rideSharing === true;
      if (activeFilter === 'luxury') return isLuxuryClass(l.classType);
      return true;
    });
  }, [liveMerged, activeFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aAvail = a.live?.isAvailable !== false;
      const bAvail = b.live?.isAvailable !== false;
      if (aAvail && !bAvail) return -1;
      if (!aAvail && bAvail) return 1;
      return (a.distKm ?? 9999) - (b.distKm ?? 9999);
    });
  }, [filtered]);

  const onlineCount = sorted.filter((l) => l.live?.isAvailable !== false).length;

  /* ── Distance used for the client-side fee preview ── */
  const previewDistanceKm = useMemo(() => {
    const t = trips[0];
    if (t?.pickupLat && t?.pickupLng && t?.dropoffLat && t?.dropoffLng) {
      return haversineKm(t.pickupLat, t.pickupLng, t.dropoffLat, t.dropoffLng);
    }
    return 0;
  }, [trips]);

  const toggleExpand = useCallback((lid: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lid)) next.delete(lid); else next.add(lid);
      return next;
    });
  }, []);

  const openBooking = useCallback((listing: Listing) => {
    setSelectedListing(listing);
    setSelectedExtras([]);
    setBookForSomeoneElse(false);
    setBooking(false);
    setBookingErr('');
    idempotencyKeyRef.current = '';
    const cat = rateCategoryOf(listing.rateType);
    setGuestCount(1);
    setDurationCount(1);
    setHourCount(1);
    setBookingStartDate(cat === 'duration' ? new Date() : null);
    setModalOpen(true);
  }, []);

  const rateCategory: RateCategory = selectedListing ? rateCategoryOf(selectedListing.rateType) : 'metered';

  // Maps the current UI selection (guestCount / durationCount) into the
  // exact field names computeBaseRideCost / the backend expect.
  const passengerParams = useMemo(() => {
    if (!selectedListing) return { passengers: 1, seats: 1 };
    const rt = resolveRateType(selectedListing.rateType);
    if (rt === 'perSeat' || rt === 'perPerson' || rt === 'perPersonPerKm') {
      return { passengers: guestCount, seats: guestCount };
    }
    return { passengers: 1, seats: Number(selectedListing.capacity) || selectedListing.seats || 1 };
  }, [selectedListing, guestCount]);

  const durationParams = useMemo(() => {
    if (!selectedListing) return {};
    const rt = resolveRateType(selectedListing.rateType);
    switch (rt) {
      case 'perDay':   return { durationDays: durationCount };
      case 'perWeek':  return { durationWeeks: durationCount };
      case 'perMonth': return { durationMonths: durationCount };
      case 'perYear':  return { durationYears: durationCount };
      case 'perHour':  return { durationHours: hourCount };
      default:         return {};
    }
  }, [selectedListing, durationCount, hourCount]);

  // Derived from bookingStartDate + durationCount, via the calendar picker.
  const bookingEndDate = useMemo(() => {
    if (!selectedListing || !bookingStartDate) return null;
    const rt = resolveRateType(selectedListing.rateType);
    const unit = DURATION_UNIT_LABEL[rt];
    if (!unit) return null;
    return addUnits(bookingStartDate, durationCount, unit);
  }, [selectedListing, bookingStartDate, durationCount]);


  /* ── Live fee preview — identical thunk/shape to BookRide.tsx, now
        rate-type aware: perPerson/perSeat feed guestCount, perDay/Week/
        Month/Year feed durationCount, everything else uses trip distance. ── */
  useEffect(() => {
    if (!selectedListing) return;
    const rt = resolveRateType(selectedListing.rateType);
    const extrasTotal = sumSelectedExtras(selectedListing.extras, selectedExtras);
    const durationMinutes = previewDistanceKm
      ? Math.max(1, Math.round((previewDistanceKm / 40) * 60))
      : 0;

    dispatch(
      calculateRideEarnings({
        rate: selectedListing.rate || 0,
        rateType: rt,
        distanceKm: previewDistanceKm,
        ...passengerParams,
        ...durationParams,
        extraCharges: extrasTotal,
        discountApplied: selectedListing.discountApplied || {},
        baseFare: selectedListing.baseFare || 0,
        perKmRate: selectedListing.perKmRate || 0,
        perMinuteRate: selectedListing.perMinuteRate || 0,
        durationMinutes,
        surgeMultiplier: selectedListing.surgeMultiplier || 1,
      })
    );
  }, [selectedListing, selectedExtras, previewDistanceKm, passengerParams, durationParams, dispatch]);

  /* ── Checkout — happens right here now. Same payload shape, same
        Stripe redirect pattern (window.location.href, same tab) as
        BookRide.tsx. No navigate() anywhere in this flow. ── */
  const handleConfirmBooking = useCallback(async () => {
    if (!selectedListing || !userId) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    setBookingErr('');
    setBooking(true);
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = makeIdempotencyKey();

    const firstTrip = trips[0];
    const lid = selectedListing.listingId || selectedListing.id;
    const rt = resolveRateType(selectedListing.rateType);
    const extraCharges = sumSelectedExtras(selectedListing.extras, selectedExtras);
    const distKm = previewDistanceKm;
    const durationMinutes = distKm ? Math.max(1, Math.round((distKm / 40) * 60)) : 0;

    // Recalculate the *displayed* estimate with the final selections —
    // the backend still computes and enforces the real charge.
    dispatch(
      calculateRideEarnings({
        rate: selectedListing.rate || 0,
        rateType: rt,
        distanceKm: distKm,
        ...passengerParams,
        ...durationParams,
        extraCharges,
        discountApplied: selectedListing.discountApplied || {},
        baseFare: selectedListing.baseFare || 0,
        perKmRate: selectedListing.perKmRate || 0,
        perMinuteRate: selectedListing.perMinuteRate || 0,
        durationMinutes,
        surgeMultiplier: selectedListing.surgeMultiplier || 1,
      })
    );

    const contact = bookForSomeoneElse
      ? {
          ...recipient,
          phone: `${recipient.dialCode}${stripDialCode(recipient.phone, recipient.dialCode)}`,
        }
      : {
          ...addr,
          phone: `${addr.dialCode}${stripDialCode(addr.phone, addr.dialCode)}`,
        };

    try {
      sessionStorage.setItem(
        PENDING_RIDE_KEY,
        JSON.stringify({
          listingId: lid,
          driverName:
            `${selectedListing.brand ? carBrandName(selectedListing.brand) : ''} ${selectedListing.model || ''}`.trim() ||
            'your driver',
          savedAt: Date.now(),
        })
      );
    } catch { /* sessionStorage may be unavailable (private mode) — non-fatal */ }

    try {
      const payload = {
        idempotencyKey: idempotencyKeyRef.current,

        riderId: userId,
        listingId: lid,

        pickupLat: firstTrip?.pickupLat ?? userLoc?.lat ?? null,
        pickupLng: firstTrip?.pickupLng ?? userLoc?.lng ?? null,
        pickupAddress: firstTrip?.pickup || 'Your Location',
        dropoffLat: firstTrip?.dropoffLat ?? null,
        dropoffLng: firstTrip?.dropoffLng ?? null,
        dropoffAddress: firstTrip?.dropoff || '',

        passengers: passengerParams.passengers ?? 1,
        seats: passengerParams.seats ?? 1,

        selectedExtras,
        discountApplied: selectedListing.discountApplied || {},
        creditsAvailable: 0,

        rideSharing: selectedListing.rideSharing || false,
        notes: '',
        idempotency_key: idempotencyKeyRef.current,
      };

      const res = await axios.post(REQUEST_RIDE_URL, payload);
      const data: any = res.data;
      if (!data?.tripId) throw new Error(`No tripId returned. Server: ${JSON.stringify(data)}`);

      // Stash contact + trip context for the payment step that fires once
      // a driver accepts — see handlePayForTrip / the activeTrip effect below.
      try {
        sessionStorage.setItem(
          PENDING_RIDE_KEY,
          JSON.stringify({
            listingId: lid,
            tripId: data.tripId,
            driverName:
              `${selectedListing.brand ? carBrandName(selectedListing.brand) : ''} ${selectedListing.model || ''}`.trim() ||
              'your driver',
            bookForSomeoneElse,
            contact,
            savedAt: Date.now(),
          })
        );
      } catch { /* sessionStorage may be unavailable (private mode) — non-fatal */ }

      setActiveTrip({ tripId: data.tripId, status: data.status || 'requested' });
      setModalOpen(false);
      setBooking(false);
      submittingRef.current = false;
    } catch (e: any) {
      console.error('❌ Request ride error:', e?.response?.data || e?.message);
      const msg = e?.response?.data?.error?.message || e?.message || 'Booking failed. Please try again.';
      setBookingErr(msg);
      setBooking(false);
      submittingRef.current = false;
      idempotencyKeyRef.current = '';
    }
  }, [
    selectedListing, userId, trips, userLoc,
    selectedExtras, bookForSomeoneElse, recipient, addr,
    passengerParams,
  ]);

  const [paying, setPaying] = useState(false);

  const handlePayForTrip = useCallback(async () => {
    if (!activeTrip?.tripId || paying) return;
    setPaying(true);
    setBookingErr('');
    try {
      let pending: any = {};
      try {
        const raw = sessionStorage.getItem(PENDING_RIDE_KEY);
        if (raw) pending = JSON.parse(raw);
      } catch { /* ignore */ }

      const contact = pending.bookForSomeoneElse ? pending.contact : addr;

      const res = await axios.post(RIDE_CHECKOUT_URL, {
        riderId: userId,
        tripId: activeTrip.tripId,
        listingId: pending.listingId,
        bookForSomeoneElse: !!pending.bookForSomeoneElse,
        contact,
      
      });

      const data: any = res.data;
      const directUrl = data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;
      if (directUrl) {
        window.location.href = directUrl;
        return;
      }
      throw new Error(`No checkout URL returned. Server: ${JSON.stringify(data)}`);
    } catch (e: any) {
      console.error('❌ Payment error:', e?.response?.data || e?.message);
      setBookingErr(e?.response?.data?.error?.message || e?.message || 'Payment failed. Please try again.');
      setPaying(false);
    }
  }, [activeTrip?.tripId, userId, addr, paying]);

  

  const handleCancelTrip = useCallback(async () => {
    if (!activeTrip?.tripId) return;
    try {
      await axios.post(CANCEL_RIDE_URL, {
        tripId: activeTrip.tripId, cancelledBy: userId, reason: 'rider_cancelled',
      });
      setActiveTrip(null);
      setBookedListing(null);
    } catch (e: any) { console.error('Cancel failed:', e.message); }
  }, [activeTrip, userId]);

  const handleDoneTrip = useCallback(() => {
    setActiveTrip(null);
    setBookedListing(null);
  }, []);

  const dismissResumeNotice = () => {
    setResumeNotice(null);
    try { sessionStorage.removeItem(PENDING_RIDE_KEY); } catch { /* ignore */ }
  };
  const resumeCancelledBooking = () => {
    const targetId = resumeNotice?.listingId;
    dismissResumeNotice();
    const match = sorted.find((l) => (l.listingId || l.id) === targetId) || sorted[0];
    if (match) {
      openBooking(match);
      setBookingErr('Your previous checkout was cancelled — review and try again.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: HEARTLAS_COLORS.bg, pb: 12 }}>
      <Box sx={{ px: 3, maxWidth: 1000, mx: 'auto', pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <img src={appLogo} alt="Heartride" style={{ width: 32, height: 32 }} />
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: 0, color: HEARTLAS_COLORS.dark, letterSpacing: -0.5 }}>
            Heartride
          </h1>
        </Box>

        {rtdbOffline && (
          <Alert severity="warning" icon={<CloudOff size={16} />} sx={{ mb: 2 }}>
            Reconnecting to live driver data…
          </Alert>
        )}

        {resumeNotice && !activeTrip && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={resumeCancelledBooking}>
                Resume
              </Button>
            }
            onClose={dismissResumeNotice}
          >
            Your last checkout with {resumeNotice.driverName} didn't finish.
          </Alert>
        )}

        {/* ── Active trip — inline tracking, no separate page ── */}
        {activeTrip ? (
          <TripTrackingCard
            trip={activeTrip}
            listing={bookedListing}
            pickupAddr={trips[0]?.pickup}
            dropoffAddr={trips[0]?.dropoff}
            fees={fees}
            money={money}
            onCancel={handleCancelTrip}
            onDone={handleDoneTrip}
            onPayNow={handlePayForTrip}
            paying={paying}
          />
        ) : (
          <>
            {/* Booking Card */}
            <Card
              sx={{
                background: HEARTLAS_COLORS.panel,
                border: `1px solid ${HEARTLAS_COLORS.border}`,
                borderRadius: 2,
                mb: 4,
                boxShadow: '0 2px 8px rgba(27,14,6,0.06)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Trip Type Toggle */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  {(['oneway', 'roundtrip', 'multitrip'] as TripType[]).map((type) => (
                    <Button
                      key={type}
                      onClick={() => handleTripTypeChange(type)}
                      sx={{
                        px: 2.5, py: 1, borderRadius: 30,
                        border: `1.5px solid ${tripType === type ? HEARTLAS_COLORS.gold : HEARTLAS_COLORS.border}`,
                        background: tripType === type ? HEARTLAS_COLORS.gold : 'transparent',
                        color: tripType === type ? '#fff' : HEARTLAS_COLORS.dark,
                        fontSize: 13, fontWeight: 700, textTransform: 'capitalize',
                        cursor: 'pointer', transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: HEARTLAS_COLORS.gold,
                          background: tripType === type ? HEARTLAS_COLORS.gold : HEARTLAS_COLORS.border,
                        },
                      }}
                    >
                      {type === 'oneway' && 'One way'}
                      {type === 'roundtrip' && 'Round trip'}
                      {type === 'multitrip' && 'Multi-trip'}
                    </Button>
                  ))}
                </Box>

                {/* Trip Legs */}
                {trips.map((trip, index) => (
                  <Box key={trip.id} sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${HEARTLAS_COLORS.divider}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: HEARTLAS_COLORS.textMuted }}>
                        Trip {index + 1}
                      </span>
                      {trips.length > 1 && (
                        <IconButton size="small" onClick={() => removeTrip(trip.id)} sx={{ ml: 'auto' }}>
                          <DeleteIcon sx={{ fontSize: 18, color: HEARTLAS_COLORS.red }} />
                        </IconButton>
                      )}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        inputRef={(el) => { pickupRefs.current[trip.id] = el; }}
                        label="Pickup location"
                        value={trip.pickup}
                        onChange={(e) =>
                          setTrips((prev) => prev.map((t) => (t.id === trip.id ? { ...t, pickup: e.target.value } : t)))
                        }
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5, background: '#fff',
                            '& fieldset': { borderColor: HEARTLAS_COLORS.border },
                            '&:hover fieldset': { borderColor: HEARTLAS_COLORS.gold },
                            '&.Mui-focused fieldset': { borderColor: HEARTLAS_COLORS.gold },
                          },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />

                      <IconButton
                        size="small"
                        onClick={() => swapLocations(trip.id)}
                        sx={{ mt: 1, color: HEARTLAS_COLORS.gold, '&:hover': { background: HEARTLAS_COLORS.border } }}
                      >
                        <SwapHorizIcon sx={{ fontSize: 20 }} />
                      </IconButton>

                      <TextField
                        inputRef={(el) => { dropoffRefs.current[trip.id] = el; }}
                        label="Dropoff location"
                        value={trip.dropoff}
                        onChange={(e) =>
                          setTrips((prev) => prev.map((t) => (t.id === trip.id ? { ...t, dropoff: e.target.value } : t)))
                        }
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5, background: '#fff',
                            '& fieldset': { borderColor: HEARTLAS_COLORS.border },
                            '&:hover fieldset': { borderColor: HEARTLAS_COLORS.gold },
                            '&.Mui-focused fieldset': { borderColor: HEARTLAS_COLORS.gold },
                          },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>
                ))}

                {tripType === 'multitrip' && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addTrip}
                    sx={{
                      color: HEARTLAS_COLORS.gold, textTransform: 'none', fontWeight: 700, fontSize: 13, mb: 2,
                      '&:hover': { background: HEARTLAS_COLORS.border },
                    }}
                  >
                    Add Trip
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Filter Chips */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, overflowX: 'auto', pb: 1 }}>
              {['all', 'nearby', 'cheap', 'share', 'luxury'].map((filter) => (
                <Button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  sx={{
                    px: 2, py: 0.8, borderRadius: 20,
                    border: `1px solid ${activeFilter === filter ? HEARTLAS_COLORS.gold : HEARTLAS_COLORS.border}`,
                    background: activeFilter === filter ? HEARTLAS_COLORS.dark : HEARTLAS_COLORS.panel,
                    color: activeFilter === filter ? '#fff' : HEARTLAS_COLORS.dark,
                    fontSize: 11, fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  {filter === 'all' && 'All Drivers'}
                  {filter === 'nearby' && 'Nearby'}
                  {filter === 'cheap' && 'Affordable'}
                  {filter === 'share' && 'Ride Share'}
                  {filter === 'luxury' && 'Luxury'}
                </Button>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: HEARTLAS_COLORS.dark, margin: 0 }}>
                Available Drivers
              </h2>
              <Chip
                label={`${sorted.length} drivers`}
                sx={{ background: HEARTLAS_COLORS.border, color: HEARTLAS_COLORS.dark, fontWeight: 700, fontSize: 11 }}
              />
            </Box>

            {/* ══════════ Drivers grid — dc- card system, identical to DriverCars.tsx ══════════ */}
            <div className={`dc-root${mounted ? ' dc-on' : ''}`}>
              {!loading && sorted.length > 0 && (
                <div className="dc-stats dc-rev" style={{ '--d': '0ms' } as any}>
                  {[
                    { label: 'Showing', val: String(sorted.length), color: H.goldDark },
                    { label: 'Available', val: String(onlineCount), color: '#15803D' },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="dc-stat-item">
                      <div className="dc-stat-val" style={{ color }}>{val}</div>
                      <div className="dc-stat-lbl">{label}</div>
                    </div>
                  ))}
                </div>
              )}

              {loading ? (
                <div className="dc-loader">
                  <Loader2 size={30} className="dc-spin" color={H.goldDark} />
                  <div className="dc-loader-txt">Loading drivers…</div>
                </div>
              ) : sorted.length === 0 ? (
                <div className="dc-empty dc-rev" style={{ '--d': '80ms' } as any}>
                  <div className="dc-empty-icon"><Car size={44} /></div>
                  <div className="dc-empty-title">No drivers available</div>
                  <div className="dc-empty-sub">Check back soon or try a different filter.</div>
                </div>
              ) : (
                <div className="dc-list">
                  {sorted.map((listing, i) => {
                    const lid = listing.listingId || listing.id;
                    const isBusy = listing.live?.isAvailable === false;
                    const statusColor = isBusy ? H.amber : '#15803D';
                    const statusLabel = isBusy ? 'Busy' : 'Available';
                    const extArr = extrasToArr(listing.extras);
                    const discKey = discKeyFromObj(listing.discountApplied);
                    const rateLabel = RATE_LABELS[listing.rateType || 'perSurge'] || '/trip';
                    const expanded = expandedIds.has(lid);

                    return (
                      <div key={lid} className="dc-row-card dc-rev" style={{ '--d': `${80 + i * 50}ms` } as any}>
                        <div className="dc-row-head" onClick={() => toggleExpand(lid)}>
                          <div className="dc-row-thumb">
                            <RowThumb listing={listing} />
                          </div>

                          <div className="dc-row-meta">
                            <div className="dc-row-title">
                              {listing.brand ? `${carBrandName(listing.brand)} ` : ''}
                              {listing.model || vehicleName(listing.vehicleType)}
                            </div>
                            <div className="dc-row-sub">
                              {listing.distKm !== undefined ? `${listing.distKm.toFixed(1)} km away` : listing.city || 'Nearby'}
                              {listing.eta !== undefined ? ` · ${listing.eta} min` : ''}
                            </div>
                            <div className="dc-status-pill">
                              <span className="dc-status-pill-dot" style={{ background: statusColor }} />
                              {statusLabel}
                            </div>
                          </div>

                          <div className="dc-row-side">
                            <div className="dc-row-rate">{money(listing.rate || 0)}</div>
                            <div className="dc-row-rate-unit">{rateLabel}</div>
                            <div className={`dc-row-chevron${expanded ? ' open' : ''}`}><ChevronDown size={16} /></div>
                          </div>
                        </div>

                        <div className={`dc-row-expand-wrap${expanded ? ' open' : ''}`}>
                          <div className="dc-row-expand-inner">
                            <div className="dc-row-expand-page">
                              <div className="dc-spec-grid">
                                <SpecItem label="Type" value={vehicleName(listing.vehicleType)} />
                                <SpecItem label="Fuel" value={listing.fuelType !== undefined ? fuelTypeName(listing.fuelType) : '—'} />
                                <SpecItem label="Transmission" value={listing.transmission !== undefined ? transmissionName(listing.transmission) : '—'} />
                                <SpecItem label="Class" value={listing.classType !== undefined ? classTypeName(listing.classType) : '—'} />
                                <SpecItem label="Size" value={listing.carSize !== undefined ? carSizeName(listing.carSize) : '—'} />
                                <SpecItem label="Seats" value={listing.capacity ? String(listing.capacity) : '—'} />
                                {listing.rating != null && (
                                  <SpecItem label="Rating" value={listing.rating.toFixed(1)} />
                                )}
                              </div>

                              <div className="dc-meta-row">
                                {(listing.photos?.length || 0) > 1 && (
                                  <div className="dc-pill" style={{ borderColor: H.border, background: H.bg, color: H.textMuted }}>
                                    <ImageIcon size={9} /> {listing.photos!.length} photos
                                  </div>
                                )}
                                {listing.rideSharing && (
                                  <div className="dc-pill" style={{ borderColor: H.blue + '44', background: H.blueSoft, color: H.blue }}>
                                    <GitMerge size={9} /> Sharing
                                  </div>
                                )}
                                {discKey !== 'none' && (
                                  <div className="dc-pill" style={{ borderColor: H.gold + '55', background: H.goldSoft, color: H.goldDark }}>
                                    <Tag size={9} /> {DISC_LABELS[discKey]} off
                                  </div>
                                )}
                              </div>

                              {listing.description && <div className="dc-desc-row">{listing.description}</div>}

                              {extArr.length > 0 && (
                                <div className="dc-extras-row">
                                  {extArr.map((e) => (
                                    <span key={e.name} className="dc-extra-tag">
                                      +{e.name} {money(e.price)}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {listing.city && (
                                <div className="dc-loc-row">
                                  <MapPin size={11} />
                                  {listing.city}{listing.country ? `, ${listing.country}` : ''}
                                </div>
                              )}

                              <div className="dc-card-actions">
                                <button
                                  className="dc-edit-btn"
                                  disabled={isBusy}
                                  onClick={(e) => { e.stopPropagation(); openBooking(listing); }}
                                >
                                  <DirectionsCarIcon sx={{ fontSize: 14 }} /> {isBusy ? 'Currently Busy' : 'Book Now'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </Box>

      {/* ── Booking modal — unchanged UI, fixed fee math ── */}
      {selectedListing && (
        <Dialog
          open={modalOpen}
          onClose={() => !booking && setModalOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: isMobileScreen
              ? {
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  m: 0,
                  width: '100%',
                  maxHeight: '85vh',
                  borderTopLeftRadius: 20,
                  borderTopRightRadius: 20,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }
              : {},
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textMuted, textTransform: 'uppercase' }}>
                Confirm your ride
              </Box>
              <Box sx={{ fontSize: 18, fontWeight: 800, color: HEARTLAS_COLORS.dark, mt: 0.5 }}>
                {selectedListing.brand ? carBrandName(selectedListing.brand) : ''} {selectedListing.model}
              </Box>
            </Box>
            <IconButton onClick={() => !booking && setModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textMuted }}>PICKUP</Box>
              <Box sx={{ fontSize: 13, fontWeight: 600, color: HEARTLAS_COLORS.dark, mt: 0.5 }}>
                {trips[0]?.pickup || 'Your location'}
              </Box>
              {trips[0]?.dropoff && (
                <>
                  <Box sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textMuted, mt: 1.5 }}>
                    DROPOFF
                  </Box>
                  <Box sx={{ fontSize: 13, fontWeight: 600, color: HEARTLAS_COLORS.dark, mt: 0.5 }}>
                    {trips[0]?.dropoff}
                  </Box>
                </>
              )}
            </Box>

            {rateCategory === 'people' && (
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textMuted, mb: 1 }}>
                  {resolveRateType(selectedListing.rateType) === 'perSeat' ? 'NUMBER OF SEATS' : 'NUMBER OF PEOPLE'}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    size="small"
                    onClick={() => setGuestCount((g) => Math.max(1, g - 1))}
                    sx={{ border: `1px solid ${HEARTLAS_COLORS.border}` }}
                  >
                    <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>−</span>
                  </IconButton>
                  <Box sx={{ fontSize: 18, fontWeight: 800, minWidth: 28, textAlign: 'center' }}>{guestCount}</Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const max = Number(selectedListing.guestCount ?? selectedListing.capacity) || 99;
                      setGuestCount((g) => Math.min(max, g + 1));
                    }}
                    sx={{ border: `1px solid ${HEARTLAS_COLORS.border}` }}
                  >
                    <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>+</span>
                  </IconButton>
                  {(selectedListing.guestCount || selectedListing.capacity) && (
                    <Box sx={{ fontSize: 11, color: HEARTLAS_COLORS.textMuted }}>
                      max {selectedListing.guestCount ?? selectedListing.capacity}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {rateCategory === 'duration' && (
              <Box sx={{ mb: 2.5 }}>
                {(() => {
                  const rt = resolveRateType(selectedListing.rateType);
                  const unit = DURATION_UNIT_LABEL[rt] || 'day';
                  const max = durationMaxFor(selectedListing, rt);
                  return (
                    <>
                      <Box sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textMuted, mb: 1 }}>
                        NUMBER OF {unit.toUpperCase()}S
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => setDurationCount((d) => Math.max(1, d - 1))}
                          sx={{ border: `1px solid ${HEARTLAS_COLORS.border}` }}
                        >
                          <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>−</span>
                        </IconButton>
                        <Box sx={{ fontSize: 18, fontWeight: 800, minWidth: 28, textAlign: 'center' }}>{durationCount}</Box>
                        <IconButton
                          size="small"
                          onClick={() => setDurationCount((d) => Math.min(max, d + 1))}
                          sx={{ border: `1px solid ${HEARTLAS_COLORS.border}` }}
                        >
                          <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>+</span>
                        </IconButton>
                        <Box sx={{ fontSize: 13, color: HEARTLAS_COLORS.textMuted }}>
                          {unit}{durationCount > 1 ? 's' : ''} · max {max}
                        </Box>
                      </Box>
                      <DurationCalendarPicker
                        startDate={bookingStartDate}
                        unitCount={durationCount}
                        unitLabel={unit}
                        onSelectStart={(d) => setBookingStartDate(d)}
                      />
                    </>
                  );
                })()}
              </Box>
            )}


{rateCategory === 'hourly' && (
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textMuted, mb: 1 }}>
                  NUMBER OF HOURS
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    size="small"
                    onClick={() => setHourCount((h) => Math.max(1, h - 1))}
                    sx={{ border: `1px solid ${HEARTLAS_COLORS.border}` }}
                  >
                    <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>−</span>
                  </IconButton>
                  <Box sx={{ fontSize: 18, fontWeight: 800, minWidth: 28, textAlign: 'center' }}>{hourCount}</Box>
                  <IconButton
                    size="small"
                    onClick={() => setHourCount((h) => Math.min(hourlyMaxFor(selectedListing), h + 1))}
                    sx={{ border: `1px solid ${HEARTLAS_COLORS.border}` }}
                  >
                    <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>+</span>
                  </IconButton>
                  <Box sx={{ fontSize: 11, color: HEARTLAS_COLORS.textMuted }}>
                    hour{hourCount > 1 ? 's' : ''} · max {hourlyMaxFor(selectedListing)}
                  </Box>
                </Box>
              </Box>
            )}


            {selectedListing.extras && Object.keys(selectedListing.extras).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: HEARTLAS_COLORS.textMuted, mb: 0.5 }}>
                  OPTIONAL EXTRAS
                </Box>
                {Object.entries(selectedListing.extras).map(([k, v]) => (
                  <FormControlLabel
                    key={k}
                    control={
                      <Checkbox
                        checked={selectedExtras.includes(k)}
                        onChange={() =>
                          setSelectedExtras((prev) => (prev.includes(k) ? prev.filter((e) => e !== k) : [...prev, k]))
                        }
                      />
                    }
                    label={`${k}  (+${money(Number(v))})`}
                    sx={{ display: 'flex', width: '100%' }}
                  />
                ))}
              </Box>
            )}

            <FormControlLabel
              control={<Checkbox checked={bookForSomeoneElse} onChange={() => setBookForSomeoneElse((v) => !v)} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonAddAlt1Icon sx={{ fontSize: 16, color: HEARTLAS_COLORS.goldDark }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Book for someone else</span>
                </Box>
              }
              sx={{ mb: 1.5 }}
            />

            {bookForSomeoneElse ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2.5 }}>
                <TextField label="Guest First Name" size="small" value={recipient.firstName} onChange={(e) => patchRecipient({ firstName: e.target.value })} />
                <TextField label="Guest Last Name" size="small" value={recipient.lastName} onChange={(e) => patchRecipient({ lastName: e.target.value })} />
                <TextField label="Guest Email" size="small" sx={{ gridColumn: '1 / -1' }} value={recipient.email} onChange={(e) => patchRecipient({ email: e.target.value })} />
                <Select size="small" value={recipient.dialCode} onChange={(e) => patchRecipient({ dialCode: e.target.value as string })}>
                  {countries.map((c: CountryInfo) => (
                    <MenuItem key={c.code} value={c.phoneCode}>{c.emoji} {c.phoneCode}</MenuItem>
                  ))}
                </Select>
                <TextField label="Guest Phone" size="small" value={recipient.phone} onChange={(e) => patchRecipient({ phone: e.target.value })} />
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2.5 }}>
                <TextField label="First Name" size="small" value={addr.firstName} onChange={(e) => patchAddr({ firstName: e.target.value })} />
                <TextField label="Last Name" size="small" value={addr.lastName} onChange={(e) => patchAddr({ lastName: e.target.value })} />
                <TextField label="Email" size="small" sx={{ gridColumn: '1 / -1' }} value={addr.email} onChange={(e) => patchAddr({ email: e.target.value })} />
                <Select size="small" value={addr.dialCode} onChange={(e) => patchAddr({ dialCode: e.target.value as string })}>
                  {countries.map((c: CountryInfo) => (
                    <MenuItem key={c.code} value={c.phoneCode}>{c.emoji} {c.phoneCode}</MenuItem>
                  ))}
                </Select>
                <TextField label="Phone" size="small" value={addr.phone} onChange={(e) => patchAddr({ phone: e.target.value })} />
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {bookingErr && (
              <Alert severity="error" sx={{ mb: 2 }}>{bookingErr}</Alert>
            )}

            <Box sx={{ background: HEARTLAS_COLORS.bg, border: `1px solid ${HEARTLAS_COLORS.border}`, borderRadius: 2, p: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ fontSize: 13 }}>Ride cost</Box>
                <Box sx={{ fontSize: 13, fontWeight: 700 }}>{money(fees?.baseRideCost)}</Box>
              </Box>

              {sumSelectedExtras(selectedListing.extras, selectedExtras) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ fontSize: 13 }}>Extras</Box>
                  <Box sx={{ fontSize: 13, fontWeight: 700 }}>
                    +{money(sumSelectedExtras(selectedListing.extras, selectedExtras))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ fontSize: 13, color: HEARTLAS_COLORS.textMuted }}>Service fee</Box>
                <Box sx={{ fontSize: 13, fontWeight: 700, color: HEARTLAS_COLORS.textMuted }}>{money(fees?.guestsFee)}</Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Box sx={{ fontSize: 14, fontWeight: 800, color: HEARTLAS_COLORS.dark }}>Estimated total</Box>
                <Box sx={{ fontSize: 20, fontWeight: 900, color: HEARTLAS_COLORS.goldDark }}>{money(fees?.totalCharged)}</Box>
              </Box>

              <Box sx={{ fontSize: 10, color: HEARTLAS_COLORS.textMuted, mt: 1 }}>
                Estimate only — final price is confirmed and charged securely via Stripe on the next step.
              </Box>
            </Box>

            <Button
              fullWidth
              disabled={booking}
              onClick={handleConfirmBooking}
              sx={{
                mt: 2,
                background: `linear-gradient(135deg, ${HEARTLAS_COLORS.red}, ${HEARTLAS_COLORS.gold})`,
                color: '#fff', py: 1.4, borderRadius: 1, fontWeight: 800, fontSize: 13,
                textTransform: 'uppercase', letterSpacing: 0.5, '&:hover': { opacity: 0.9 },
              }}
            >
              {booking ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <>
                  <DirectionsCarIcon sx={{ mr: 1, fontSize: 16 }} />
                  Continue to Payment
                </>
              )}
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

/* ─── Inline trip tracking — replaces the whole separate BookRide page for
      the post-payment leg of the flow. ─────────────────────────────────── */
function TripTrackingCard({
  trip, listing, pickupAddr, dropoffAddr, fees, money, onCancel, onDone, onPayNow, paying,
}: {
  trip: any; listing: any; pickupAddr?: string; dropoffAddr?: string;
  fees: any; money: (n: number | undefined | null) => string;
  onCancel: () => void; onDone: () => void; onPayNow: () => void; paying: boolean;
}) {
  const status = trip?.status || 'requested';
  const cfg = TRIP_STATUS_CFG[status] || TRIP_STATUS_CFG.requested;
  const isDone = status === 'completed' || status === 'cancelled' || status === 'no_driver';
  const canCancel = ['requested', 'accepted', 'finding_driver'].includes(status);

  return (
    <Card sx={{ border: `1px solid ${HEARTLAS_COLORS.border}`, borderRadius: 2, mb: 4, boxShadow: '0 2px 8px rgba(27,14,6,0.06)' }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 2,
            borderRadius: 2, border: `1.5px solid ${cfg.color}40`, background: `${cfg.color}0D`,
          }}
        >
          {status === 'completed' ? <Flag size={22} color={cfg.color} /> :
            status === 'cancelled' || status === 'no_driver' ? <AlertCircle size={22} color={cfg.color} /> :
            status === 'ongoing' ? <Navigation2 size={22} color={cfg.color} /> :
            <Car size={22} color={cfg.color} />}
          <Box>
            <Box sx={{ fontSize: 15, fontWeight: 800, color: cfg.color }}>{cfg.label}</Box>
            <Box sx={{ fontSize: 12, color: HEARTLAS_COLORS.textMuted }}>{cfg.sub}</Box>
          </Box>
        </Box>

        {listing && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 2, background: HEARTLAS_COLORS.bg,
              border: `1px solid ${HEARTLAS_COLORS.border}`, borderRadius: 2, p: 2, mb: 2,
            }}
          >
            <Box
              sx={{
                width: 46, height: 46, borderRadius: '50%', background: HEARTLAS_COLORS.goldSoft,
                border: `2px solid ${HEARTLAS_COLORS.border}`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontWeight: 700, color: HEARTLAS_COLORS.goldDark, flexShrink: 0,
              }}
            >
              {(listing.model || 'D')[0]}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontSize: 14, fontWeight: 800, color: HEARTLAS_COLORS.dark }}>
                {listing.brand ? carBrandName(listing.brand) : ''} {listing.model}
              </Box>
              <Box sx={{ fontSize: 12, color: HEARTLAS_COLORS.textMuted }}>{listing.licensePlate}</Box>
            </Box>
            {listing.phone && (
              <IconButton component="a" href={`tel:${listing.phone}`} sx={{ background: '#fff', border: `1px solid ${HEARTLAS_COLORS.border}` }}>
                <PhoneCall size={15} />
              </IconButton>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapPin size={12} color={HEARTLAS_COLORS.dark} />
            <Box sx={{ fontSize: 12, fontWeight: 600, color: HEARTLAS_COLORS.dark }}>{pickupAddr || 'Pickup'}</Box>
          </Box>
          {dropoffAddr && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapPin size={12} color={HEARTLAS_COLORS.red} />
              <Box sx={{ fontSize: 12, fontWeight: 600, color: HEARTLAS_COLORS.dark }}>{dropoffAddr}</Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: HEARTLAS_COLORS.bg, border: `1px solid ${HEARTLAS_COLORS.border}`, borderRadius: 2, p: 2, mb: 2,
          }}
        >
          <Box sx={{ fontSize: 12, color: HEARTLAS_COLORS.textMuted }}>
            {status === 'completed' ? 'Total charged' : 'Estimated total'}
          </Box>
          <Box sx={{ fontSize: 18, fontWeight: 800, color: HEARTLAS_COLORS.goldDark }}>{money(fees?.totalCharged)}</Box>
        </Box>

{status === 'accepted' && !trip.isPaid && (
          <Button
            fullWidth
            disabled={paying}
            onClick={onPayNow}
            sx={{
              mb: 1,
              background: `linear-gradient(135deg, ${HEARTLAS_COLORS.red}, ${HEARTLAS_COLORS.gold})`,
              color: '#fff', fontWeight: 800,
            }}
          >
            {paying ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Pay & Confirm Ride'}
          </Button>
        )}

        {canCancel && (
          <Button
            fullWidth
            onClick={onCancel}
            sx={{ mb: 1, border: `1px solid ${HEARTLAS_COLORS.red}55`, color: HEARTLAS_COLORS.red, fontWeight: 700 }}
          >
            Cancel Ride
          </Button>
        )}

     
        {isDone && (
          <Button
            fullWidth
            onClick={onDone}
            sx={{
              background: HEARTLAS_COLORS.dark, color: '#fff', fontWeight: 800,
              '&:hover': { background: HEARTLAS_COLORS.red },
            }}
          >
            {status === 'no_driver' ? <><RotateCcw size={14} style={{ marginRight: 8 }} /> Try Again</>
              : <><HomeIcon size={14} style={{ marginRight: 8 }} /> Back to Search</>}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default RiderHome;